import ProductBuyCardSizes from "@components/commerce/product/right-panel/buy-card/sizes/ProductBuyCardSizes";
import AddToWishlist from "@components/commerce/wishlist/AddToWishlist";
import type { ProductData, ProductPrice, ProductVariant } from "@graphql/generated/components";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Attributes, CustomAttributes } from "@lib/enums/ProductAttributes";
import { getBackgroundColorValue, getProductAttributeValue } from "@lib/utils/attributesUtils";
import { isSizeProbablyOneSize } from "@lib/utils/pdp/pdpShoeSizes";
import { Text } from "@ui/components/content/text/Text";
import { Money } from "@ui/components/data-display/money/Money";
import { Button } from "@ui/components/forms/button/Button";
import { Box } from "@ui/components/layout/box/Box";
import { Flex } from "@ui/components/layout/flex/Flex";
import { NextImage } from "@ui/components/media-and-icons/image/NextImage";
import { Collapse } from "@ui/components/other/transitions/Collapse";
import { useOutsideClick } from "@ui/hooks/useOutsideClick";
import { isEmpty } from "lodash-es";
import type { MutableRefObject } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { styles } from "./ProductBuyCardSticky.styles";
import { useStore } from "@store/store";

interface ProductBuyCardStickyProps {
    locale: string;
    product: ProductData;
    initialProductPrice: ProductPrice;
    //selectedVariant: ProductVariant;
    onAddToCart: (selectedVariant: ProductVariant) => Promise<void>;
    onAddToWishlist: (selectedVariant: ProductVariant) => Promise<void>;
    isAddingToWishlist: boolean;
    isOpen?: boolean;
    headlineProductNameRef?: MutableRefObject<HTMLHeadingElement>;
}

export const ProductBuyCardSticky = ({
    locale,
    product,
    initialProductPrice,
    onAddToCart,
    onAddToWishlist,
    isAddingToWishlist,
    isOpen,
    headlineProductNameRef,
}: ProductBuyCardStickyProps) => {
    const initialRef = useRef(null);
    const { getMultiple } = useMicrocopy();
    const [showSizes, setShowSizes] = useState(false);
    const [showSelectSizeError, setShowSelectSizeError] = useState(false);
    const selectedVariant = useStore((state) => state.data.pdpState.selectedVariant);
    const price = selectedVariant ? selectedVariant?.price : initialProductPrice;
    useOutsideClick({
        ref: initialRef,
        handler: (event) => {
            event.stopPropagation();
            // If user clicks outside the Tooltip, it closes, if the click is on it, nothing happens, will be handled by the tooltip itself
            if (event.target !== initialRef.current) setShowSizes(false);
        },
    });

    useEffect(() => {
        setShowSizes(false);
        setShowSelectSizeError(false);
    }, [isOpen]);

    const { name, masterVariant, variants } = product;
    const productImage = masterVariant?.images?.[0]?.url;
    const { descriptiveHeading, recommendedPrice } = useMemo(() => {
        return {
            descriptiveHeading: getProductAttributeValue(
                masterVariant.attributesRaw,
                Attributes.descriptiveHeading,
                locale
            ),
            recommendedPrice: getProductAttributeValue(
                price?.custom?.customFieldsRaw,
                CustomAttributes?.recommendedPrice
            ),
        };
    }, [masterVariant.attributesRaw, price]);
    const isProbablyOneSize = isSizeProbablyOneSize(variants);

    const microcopies = useMemo(() => {
        return getMultiple(CommonCMS.page, [
            CommonCMS.PDP.addToCart,
            CommonCMS.PDP.pleaseSelectSize,
        ]);
    }, []);
    const selectSizeMicrocopy = microcopies["PDP.pleaseSelectSize"];
    const addToCartMicrocopy = microcopies["PDP.addToCart"];

    const isChatInProgress = !!document?.querySelector(
        "button.embeddedServiceSidebarMinimizedDefaultUI"
    );

    const handleClick = () => {
        // Open the Size selection and Size Guide for products that are not categorized as ‘One-Size‘
        if (!showSizes && !isProbablyOneSize) {
            setShowSizes(true);
            return;
        }
        if (!selectedVariant) {
            setShowSelectSizeError(true);
            return;
        }
        onAddToCart(selectedVariant);
        setShowSizes(false);
    };

    return (
        <Box
            sx={{
                justifyContent: ["center", "flex-end"],
                py: [3, 0],
                pt: [0, 0, 12, 40],
                px: [3, 0],
                width: "100%",
                bottom: 0,
                position: "sticky",
                display: "flex",
                zIndex: 2,
                left: "100%",
                // For desktop we need to set 1px to make the sticky element as an anchor and not take space
                // In the natural flow of the DOM
                h: ["100%", "100%", "1px"],
            }}
            opacity={isOpen ? 1 : 0}
            visibility={isOpen ? "visible" : "hidden"}
            transition={`${isOpen ? "opacity" : "all"} 0.2s`}
        >
            <Box
                mt={0}
                ref={initialRef}
                sx={{
                    ...styles.modalContent,
                    bottom: isChatInProgress ? [7, 6, 4, 4] : 0,
                }}
            >
                <Flex sx={{ ...styles.productDataContainer }}>
                    <Box
                        height={"100%"}
                        backgroundColor={getBackgroundColorValue(masterVariant.attributesRaw)}
                    >
                        <NextImage
                            fill
                            objectFit={"cover"}
                            src={productImage}
                            aspectRatio={"4/5"}
                            sizes={["10vw", "10vw", "10vw", "8vw"]}
                            alt={name}
                        />
                    </Box>
                    <Flex py={4} pr={4} direction="column" justifyContent={"space-between"}>
                        <Flex direction={"column"} gap={1}>
                            <Text fontSize={"mobileBodyB2"} fontWeight={"bold"}>
                                {name}
                            </Text>
                            {descriptiveHeading && (
                                <Text
                                    fontSize={"mobileBodyB2"}
                                    color="gray.500"
                                    fontWeight={"bold"}
                                >
                                    {descriptiveHeading}
                                </Text>
                            )}
                        </Flex>
                        <Flex gap={2}>
                            <Money fontWeight="bold" locale={locale} data={price?.value} />
                            {!isEmpty(recommendedPrice) && (
                                <Money
                                    fontWeight="bold"
                                    textDecoration="line-through"
                                    color="gray.500"
                                    locale={locale}
                                    data={recommendedPrice}
                                />
                            )}
                        </Flex>
                    </Flex>
                </Flex>
                <Collapse
                    in={showSizes}
                    // We need this initial value minimal to allow animate the height
                    startingHeight={"0.1px"}
                    animateOpacity={false}
                    style={{
                        opacity: 1,
                    }}
                >
                    <Box display={["none", "block"]} bg="white" p={6}>
                        <ProductBuyCardSizes
                            variants={variants}
                            masterVariant={masterVariant}
                            isStickyModal={true}
                        />
                        {!selectedVariant && showSelectSizeError && (
                            <Box mt="3">
                                <Text color="red.500" fontWeight={"bold"} fontSize={"mobileBodyB2"}>
                                    {selectSizeMicrocopy}
                                </Text>
                            </Box>
                        )}
                    </Box>
                </Collapse>
                <Flex sx={styles.submitContainer}>
                    <Button
                        colorScheme="black"
                        sx={styles.submitButton}
                        size="md"
                        display={{ base: "none", lg: "block" }}
                        fontSize="desktopBodyB2"
                        onClick={handleClick}
                        data-testid="add-to-cart-button"
                    >
                        <span>{selectedVariant ? addToCartMicrocopy : selectSizeMicrocopy}</span>
                    </Button>
                    {/* The behaviour of the buttons will change based on mobile or desktop. To avoid useThemeMediaQuery hook and layout shift, we will just hide/show buttons based on CSS */}
                    <Button
                        colorScheme="black"
                        sx={{ ...styles.submitButton, bg: "rgba(0, 0, 0, .92)" }}
                        size="md"
                        display={{ base: "flex", lg: "none" }}
                        fontSize={"desktopBodyB2"}
                        onClick={() => {
                            headlineProductNameRef.current.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                            });
                        }}
                        data-testid="add-to-cart-button"
                    >
                        <span>{selectedVariant ? addToCartMicrocopy : selectSizeMicrocopy}</span>
                    </Button>
                    <AddToWishlist
                        product={selectedVariant}
                        onAddToWishlist={onAddToWishlist}
                        isAddingToWishlist={isAddingToWishlist}
                        bg={["rgba(0, 0, 0, .92)", "black"]}
                    />
                </Flex>
            </Box>
        </Box>
    );
};

export default ProductBuyCardSticky;
